import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import { GetSelectedLanguage } from "../helper/Method";
import {
  COUNTRIES,
  normalizePhone,
  parsePhoneNumber,
  formatE164,
} from "./phoneUtils";
import "flag-icons/css/flag-icons.min.css";
import "../member/UpdateMember/PhoneNumberInput.css";

const DEFAULT_COUNTRY_CODE = "PK";

const toOption = (c) => ({
  value: c.code,
  code: c.code,
  name: c.name,
  dialCode: String(c.dialCode),
});

const ALL_OPTIONS = COUNTRIES.map(toOption);
// Pakistan is pinned in its own group on top, the rest follow alphabetically.
const GROUPED_OPTIONS = [
  { label: "pinned", options: ALL_OPTIONS.filter((o) => o.code === DEFAULT_COUNTRY_CODE) },
  { label: "all", options: ALL_OPTIONS.filter((o) => o.code !== DEFAULT_COUNTRY_CODE) },
];

const findOption = (code) => ALL_OPTIONS.find((o) => o.code === code) || null;

// Resolve an incoming value into { country, number, legacy }.
const resolveValue = (value, defaultCountry) => {
  if (!value) return { country: defaultCountry, number: "", legacy: false };
  const parsed = parsePhoneNumber(value, ALL_OPTIONS);
  if (parsed.country) {
    const match =
      ALL_OPTIONS.find((o) => o.dialCode === String(parsed.country.dialCode || parsed.country.phoneCode)) ||
      null;
    return { country: match, number: parsed.nationalNumber, legacy: false };
  }
  // Old value without a country code: show it on the default country (Pakistan) without a
  // leading 0 and ask the user to confirm. It is not rewritten until the user changes it.
  return {
    country: defaultCountry,
    number: String(parsed.nationalNumber).replace(/\D/g, "").replace(/^0+/, ""),
    legacy: !!parsed.isLegacy,
  };
};

const filterOption = (option, input) => {
  const q = input.trim().toLowerCase().replace(/^\+/, "");
  if (!q) return true;
  const { name, code, dialCode } = option.data;
  return name.toLowerCase().includes(q) || code.toLowerCase() === q || dialCode.startsWith(q);
};

const Flag = ({ code }) => (
  <span className={`fi fi-${code.toLowerCase()} pni-flag`} aria-hidden="true" />
);

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <Flag code={props.data.code} />
    <span className="pni-dial">+{props.data.dialCode}</span>
  </components.SingleValue>
);

const Option = (props) => (
  <components.Option {...props}>
    <Flag code={props.data.code} />
    <span className="pni-name">{props.data.name}</span>
    <span className="pni-dial-badge">+{props.data.dialCode}</span>
  </components.Option>
);

// Group headings are only used as a divider between the pinned country and the list.
const GroupHeading = () => <div className="pni-divider" />;

export default function PhoneNumberInput({
  value = "",
  onChange,
  onBlur,
  name = "phoneNumber",
  disabled = false,
  invalid = false,
  errorMessage = "",
  placeholder,
  className = "",
  style,
  defaultCountry = DEFAULT_COUNTRY_CODE, // ISO code, e.g. the person's own country
}) {
  const { t } = useTranslation();
  const isUrdu = GetSelectedLanguage() === "ur";
  const defaultOption = useMemo(
    () => findOption(defaultCountry) || findOption(DEFAULT_COUNTRY_CODE),
    [defaultCountry]
  );

  const [state, setState] = useState(() => resolveValue(value, defaultOption));
  const [touched, setTouched] = useState(false);
  const lastEmitted = useRef(value || "");
  const { country, number, legacy } = state;

  // Re-sync only when the parent changes the value (not for our own echo),
  // otherwise an emptied number would reset the selected country.
  useEffect(() => {
    if ((value || "") === lastEmitted.current) return;
    lastEmitted.current = value || "";
    setState(resolveValue(value, defaultOption));
  }, [value, defaultOption]);

  const emit = (nextCountry, nextNumber, nextLegacy) => {
    const result =
      nextCountry && nextNumber
        ? formatE164(nextCountry.dialCode, nextNumber)
        : nextLegacy
        ? nextNumber
        : "";
    if (result === lastEmitted.current) return;
    lastEmitted.current = result;
    if (onChange) onChange({ target: { name, value: result } });
  };

  const handleCountryChange = (option) => {
    const cleaned = number.replace(/\D/g, "").replace(/^0+/, "");
    setState({ country: option, number: cleaned, legacy: false });
    setTouched(true);
    emit(option, cleaned, false);
  };

  const handleNumberChange = (e) => {
    const raw = e.target.value;
    setTouched(true);

    // A pasted international number selects its country automatically.
    if (/^(\+|00)/.test(raw.trim())) {
      const next = resolveValue(raw, defaultOption);
      if (next.country) {
        setState(next);
        emit(next.country, next.number, false);
        return;
      }
    }

    let digits = raw.replace(/\D/g, "");
    if (country && country.dialCode === "92") digits = digits.replace(/^0+/, "");
    setState({ country, number: digits, legacy: legacy && !country });
    emit(country, digits, legacy && !country);
  };

  const handleBlur = (e) => {
    setTouched(true);
    if (onBlur) onBlur(e);
  };

  // width of the country trigger grows with the length of the dial code
  const triggerWidth = 66 + ((country?.dialCode || "92").length + 1) * 9;

  const e164 = country && number ? formatE164(country.dialCode, number) : "";
  const pkMobileLength =
    country?.dialCode === "92" && number.startsWith("3") && number.length !== 10;
  const hasError =
    invalid ||
    (touched && !!number && (!country || !normalizePhone(e164) || pkMobileLength));

  const message =
    errorMessage ||
    (pkMobileLength
      ? isUrdu
        ? "پاکستانی موبائل نمبر 10 ہندسوں کا ہونا چاہیے (3 سے شروع)"
        : "Pakistani mobile numbers have 10 digits and start with 3"
      : !country
      ? isUrdu
        ? "براہ کرم ملک کا کوڈ منتخب کریں"
        : "Please select a country code"
      : t("member_phone_error_message"));

  return (
    <div className={`pni ${className}`} style={style}>
      <div
        className={`pni-group${hasError ? " is-invalid" : ""}${disabled ? " is-disabled" : ""}`}
        dir="ltr"
      >
        <Select
          className="pni-country"
          classNamePrefix="pni-select"
          options={GROUPED_OPTIONS}
          value={country}
          onChange={handleCountryChange}
          isDisabled={disabled}
          isClearable={false}
          isSearchable
          filterOption={filterOption}
          placeholder="+ Code"
          noOptionsMessage={() => (isUrdu ? "کوئی ملک نہیں ملا" : "No country found")}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            container: (base) => ({ ...base, flex: `0 0 ${triggerWidth}px` }),
          }}
          components={{
            SingleValue,
            Option,
            GroupHeading,
            IndicatorSeparator: null,
          }}
        />
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          name={name}
          value={number}
          onChange={handleNumberChange}
          onBlur={handleBlur}
          disabled={disabled}
          className="pni-number"
          placeholder={placeholder || (isUrdu ? "فون نمبر" : "Phone number")}
        />
      </div>
      {legacy && (
        <div className="pni-hint">
          {isUrdu ? "اس نمبر کے ساتھ ملک کا کوڈ محفوظ نہیں ہے، براہ کرم تصدیق کریں" : "Saved without a country code - please confirm the country"}
        </div>
      )}
      {hasError && <div className="pni-error">{message}</div>}
    </div>
  );
}
